import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = logged out, obj = logged in
  const [error, setError] = useState("");

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = async (username, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { username, password });
      if (data?.token) localStorage.setItem("gml_token", data.token);
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  };

  const register = async (username, game_id, whatsapp, password, platform = "mobile") => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", { username, game_id, whatsapp, password, platform });
      if (data?.token) localStorage.setItem("gml_token", data.token);
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* noop */ }
    localStorage.removeItem("gml_token");
    setUser(false);
  };

  /** Inject a freshly-issued JWT (Google sign-in path) and refresh the user. */
  const hydrateFromToken = useCallback(async (token, userObj = null) => {
    if (!token) return null;
    localStorage.setItem("gml_token", token);
    if (userObj) {
      setUser(userObj);
      return userObj;
    }
    return await refreshMe();
  }, [refreshMe]);

  return (
    <AuthContext.Provider value={{ user, error, login, register, logout, refreshMe, hydrateFromToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
